<template>
  <div class="flex flex-col">
    <div class="flex flex-wrap items-center justify-between gap-3 mb-4">
      <p class="text-sm text-gray-600 flex-1 min-w-0">
        Manage the Infobip auth key and base URL used by the notification service to send SMS.
      </p>
      <div class="flex flex-wrap items-center gap-2 shrink-0">
        <button type="button" class="btn" :disabled="loading" @click="loadAll">
          <i class="fa-solid fa-rotate-right" :class="{ 'fa-spin': loading }"></i>
          <span>Refresh</span>
        </button>
        <button
          v-if="loaded && credentialsConfigured"
          type="button"
          class="btn-sm-danger"
          :disabled="deleting || loading || saving"
          @click="confirmDelete"
        >
          <i v-if="deleting" class="fa fa-spinner fa-spin"></i>
          <i v-else class="fa-solid fa-trash"></i>
          <span>Delete credentials</span>
        </button>
      </div>
    </div>

    <div class="relative min-h-[280px]">
      <div
        v-if="loading && !loaded"
        class="absolute inset-0 z-10 flex flex-col items-center justify-center rounded border border-gray-200 bg-white/90"
      >
        <i class="fa-solid fa-circle-notch fa-spin text-3xl text-primary"></i>
        <p class="mt-3 text-sm text-gray-500">Loading Infobip credentials...</p>
      </div>

      <div :class="{ invisible: loading && !loaded }">
        <div class="flex flex-wrap items-center gap-2 mb-4">
          <span
            class="px-2 py-0.5 rounded-full text-xs font-semibold"
            :class="credentialsConfigured ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'"
          >
            {{ credentialsConfigured ? "Credentials configured" : "No credentials configured" }}
          </span>
        </div>

        <form class="max-w-lg space-y-4" @submit.prevent="saveCredentials">
          <div>
            <label class="text-xs font-semibold text-gray-600 uppercase tracking-wide">Auth key</label>
            <div class="relative mt-1">
              <input
                v-model="form.authKey"
                :type="showAuthKey || authKeyIsStoredMask ? 'text' : 'password'"
                autocomplete="new-password"
                placeholder="Enter auth key"
                class="w-full px-3 py-2 pr-10 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-gray-50"
                :class="{ 'font-mono text-gray-500': authKeyIsStoredMask }"
                :disabled="loading || saving"
                required
                @focus="selectStoredMask"
              />
              <button
                v-if="!authKeyIsStoredMask"
                type="button"
                class="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-primary"
                :title="showAuthKey ? 'Hide auth key' : 'Show auth key'"
                @click="showAuthKey = !showAuthKey"
              >
                <i :class="showAuthKey ? 'fa-solid fa-eye-slash' : 'fa-solid fa-eye'"></i>
              </button>
            </div>
          </div>
          <div>
            <label class="text-xs font-semibold text-gray-600 uppercase tracking-wide">Base URL</label>
            <input
              v-model="form.baseUrl"
              type="url"
              autocomplete="off"
              placeholder="https://xxxxx.api.infobip.com"
              class="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-gray-50 mt-1"
              :disabled="loading || saving"
              required
            />
          </div>
          <div class="flex flex-wrap items-center gap-2 pt-1">
            <button
              type="submit"
              class="btn-primary"
              :disabled="loading || saving || deleting || !canSave"
            >
              <i v-if="saving" class="fa fa-spinner fa-spin"></i>
              <i v-else class="fa-solid fa-save"></i>
              <span>{{ credentialsConfigured ? "Update credentials" : "Save credentials" }}</span>
            </button>
            <span v-if="!canSave" class="text-xs text-gray-500">
              {{ saveHint }}
            </span>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useConfirmDialog, useToast } from "@/composables";
import { extractErrorMessage } from "./uraApi";
import {
  deleteInfobipCredentials,
  fetchInfobipCredentials,
  saveInfobipCredentials,
} from "./infobipApi";

const { successToast, errorToast, warningToast } = useToast();
const { showConfirm } = useConfirmDialog();

const loading = ref(false);
const saving = ref(false);
const deleting = ref(false);
const loaded = ref(false);
const credentialsConfigured = ref(false);
const maskedAuthKey = ref<string | null>(null);
const showAuthKey = ref(false);
const form = ref({
  authKey: "",
  baseUrl: "",
});

const isValidBaseUrl = (value: string) => {
  try {
    return new URL(value).protocol === "https:";
  } catch {
    return false;
  }
};

const authKeyIsStoredMask = computed(
  () => maskedAuthKey.value != null && form.value.authKey === maskedAuthKey.value
);

const authKeyIsNew = computed(() => {
  const value = form.value.authKey.trim();
  return value !== "" && value !== maskedAuthKey.value && !value.startsWith("***");
});

const baseUrlIsValid = computed(() => isValidBaseUrl(form.value.baseUrl.trim()));

const canSave = computed(() => authKeyIsNew.value && baseUrlIsValid.value);

const saveHint = computed(() => {
  if (!authKeyIsNew.value) {
    return maskedAuthKey.value
      ? "Enter the full auth key to save; the masked key cannot be saved."
      : "Enter the auth key to save.";
  }
  return "Enter a valid https base URL to save.";
});

const selectStoredMask = (event: FocusEvent) => {
  if (authKeyIsStoredMask.value) (event.target as HTMLInputElement).select();
};

const applyCredentials = (masked: string | null, baseUrl: string | null, configured: boolean) => {
  credentialsConfigured.value = configured;
  maskedAuthKey.value = masked;
  form.value = { authKey: masked || "", baseUrl: baseUrl || "" };
};

const loadCredentials = async () => {
  try {
    const credentials = await fetchInfobipCredentials();
    applyCredentials(credentials.maskedAuthKey, credentials.baseUrl, credentials.configured);
  } catch (error: unknown) {
    const err = error as { response?: { status?: number } };
    if (err.response?.status === 404) {
      applyCredentials(null, null, false);
    } else {
      errorToast("Error", extractErrorMessage(error, "Could not load Infobip credentials"));
    }
  }
};

const loadAll = async () => {
  loading.value = true;
  try {
    await loadCredentials();
  } finally {
    loading.value = false;
    loaded.value = true;
  }
};

const saveCredentials = async () => {
  if (!canSave.value) {
    warningToast("Validation", saveHint.value);
    return;
  }
  saving.value = true;
  try {
    await saveInfobipCredentials({
      authKey: form.value.authKey.trim(),
      baseUrl: form.value.baseUrl.trim(),
    });
    successToast("Success", "Infobip credentials saved");
    showAuthKey.value = false;
    await loadAll();
  } catch (error: unknown) {
    errorToast("Error", extractErrorMessage(error, "Could not save Infobip credentials"));
  } finally {
    saving.value = false;
  }
};

const confirmDelete = async () => {
  const message =
    "Delete Infobip credentials? This removes the auth key and base URL. If Infobip is the active SMS gateway, every SMS will fail until new credentials are saved or another gateway is selected.";
  const confirmed = await showConfirm(message, {
    title: "Delete Infobip Credentials",
    variant: "danger",
    okTitle: "Delete",
    cancelTitle: "Cancel",
  });
  if (!confirmed) return;

  deleting.value = true;
  try {
    await deleteInfobipCredentials();
    successToast("Success", "Infobip credentials deleted");
    applyCredentials(null, null, false);
    showAuthKey.value = false;
  } catch (error: unknown) {
    errorToast("Error", extractErrorMessage(error, "Could not delete Infobip credentials"));
  } finally {
    deleting.value = false;
  }
};

onMounted(() => {
  loadAll();
});

defineExpose({ refresh: loadAll });
</script>
